/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useState } from 'react'
import { observer } from 'mobx-react'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
// img
import { ArrowIco } from '@shared/Icon/ArrowIcon'
// css
import { SelectMonth } from '@styles/button.style'
import { AdvButton } from '@components/Button'
import { appScrollTo } from '@utils/utils'
import { Schema$UserPickExtend } from '@interface/user-pick'
import { ListView } from 'antd-mobile'
import dayjs from 'dayjs'
import { UserPickItem } from '@views/Home/UserPick/Components/UserPickItem'
import { AdvEmpty } from '@components/AdvEmpty'
import { ALL_MEMBER_USER_PICK_KEY, CLASSNAMES, EMPTY_ICON } from '@constants/index'
import { UserPickMemberFilter } from '@views/Home/UserPick/Components/UserPickMemberFilter'
import { COMMUNICATION_BLOCK_TYPE, FROM_VIEW_TYPE } from '@v2/constants/common'
import { runInAction } from 'mobx'

const dataSource = new ListView.DataSource({
  rowHasChanged: (row1, row2) => row1 !== row2,
})

const TodayUserPick: React.FC = observer(() => {
  const { userPickStore } = useRootStore()
  const { userPickRecentHitRateMap } = userPickStore
  const [month, setMonth] = useState<string>(dayjs().format('YYYY-MM'))
  const [selectedUserPromiseIds, setSelectedUserPromiseIds] = useState<string[]>([ALL_MEMBER_USER_PICK_KEY])

  const {
    monthlyBestUserPicks,
    monthlyBestUserPickHasMore,
  } = userPickStore

  const fetch = (reset = false) => {
    userPickStore.fetchMonthlyBestUserPicks(month, selectedUserPromiseIds, reset).catch(console.error)
  }

  const onChangeMonth = (baseMonth: string): void => {
    const wrapElem = document.querySelector('.today-user-pick.user-pick-user-filter-wrap > .ant-radio-group')
    if (wrapElem) {
      wrapElem.classList.add(CLASSNAMES.ADV_DISABLE_SCROLL)
    }
    userPickStore.fetchMonthlyBestUserPicks(baseMonth, [ALL_MEMBER_USER_PICK_KEY], true).then(() => {
      appScrollTo(1)
      if (wrapElem) {
        wrapElem.scrollLeft = 0
        wrapElem.classList.remove(CLASSNAMES.ADV_DISABLE_SCROLL)
      }
    }).catch((err) => {
      console.error(err)
      if (wrapElem) {
        wrapElem.scrollLeft = 0
        wrapElem.classList.remove(CLASSNAMES.ADV_DISABLE_SCROLL)
      }
    })
  }

  useOnMount(() => {
    appScrollTo(1)
    userPickStore.saveLastBestUserPickIdToLocal()
    fetch(true)
  })

  useOnUnmount(() => {
    userPickStore.resetMonthlyBestUserPicks()
  })

  const isPreviousButtonDisabled = () => dayjs(month).isBefore(dayjs().startOf('months'))
  const isNextButtonDisabled = () => dayjs().startOf('months').isSame(dayjs(month))

  const onClickPreviousButton = () => {
    if (isPreviousButtonDisabled()) {
      return
    }

    const newMonth = dayjs(month).add(-1, 'months').format('YYYY-MM')
    setMonth(newMonth)
    setSelectedUserPromiseIds([ALL_MEMBER_USER_PICK_KEY])
    onChangeMonth(newMonth)
  }

  const onClickNextButton = () => {
    if (isNextButtonDisabled()) {
      return
    }

    const newMonth = dayjs(month).add(1, 'months').format('YYYY-MM')
    setMonth(newMonth)
    setSelectedUserPromiseIds([ALL_MEMBER_USER_PICK_KEY])
    onChangeMonth(newMonth)
  }

  const onChangeMemberFilter = (selectedUserPromiseIds: string[]): void => {
    setSelectedUserPromiseIds(selectedUserPromiseIds)
    userPickStore.fetchMonthlyBestUserPicks(month, selectedUserPromiseIds, true).then(() => {
      appScrollTo(1)
    }).catch(console.error)
  }

  const onUserBlockSuccessHandler = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    runInAction(() => {
      const isUserBlock = !_.isEmpty(blockTypes)
      const isPickBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.USER_PICK)

      userPickStore.monthlyBestUserPicks = userPickStore.setUserBlock(
        monthlyBestUserPicks,
        userPromiseId,
        isUserBlock,
        isPickBlock,
      )
    })
  }

  const renderHeader = () => (
    <Fragment>
      <SelectMonth isSingleBtn>
        <AdvButton
          width={40}
          height={40}
          ico={<ArrowIco/>}
          icoColor="darkTxt"
          icoRotate={90}
          icoSize={12}
          className={`prev-btn ${isPreviousButtonDisabled() ? 'disabled' : ''}`}
          onClick={onClickPreviousButton}
        />
        <div className="date">
          <span className="month">{dayjs(month).format('MM')}월</span>
        </div>
        <AdvButton
          width={40}
          height={40}
          ico={<ArrowIco/>}
          icoColor="darkTxt"
          icoRotate={-90}
          icoSize={12}
          className={`next-btn ${isNextButtonDisabled() ? 'disabled' : ''}`}
          onClick={onClickNextButton}
        />
      </SelectMonth>
      <UserPickMemberFilter
        className="popup today-user-pick"
        fromView={FROM_VIEW_TYPE.TODAY_USER_PICK_POPUP}
        selectedUserPromiseIds={selectedUserPromiseIds}
        onChange={onChangeMemberFilter}
      />
    </Fragment>
  )

  const renderRow = (row: Schema$UserPickExtend): React.ReactElement => {
    const recentHitRate = userPickRecentHitRateMap ? userPickRecentHitRateMap[row.pickOwner.userPromiseId] : undefined
    return <UserPickItem
      userPick={row}
      recentHitRate={recentHitRate}
      showMatchInfoSimple
      onUserBlockSuccessHandler={onUserBlockSuccessHandler}
    />
  }

  const renderFooter = (): React.ReactElement => {
    let footer
    if (_.size(monthlyBestUserPicks) > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.TODAY_USER_PICK_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const endReachedHandler = () => {
    if (monthlyBestUserPickHasMore) {
      fetch()
    }
  }

  return (
    <ListView
      css={{ height: '100%' }}
      dataSource={dataSource.cloneWithRows(monthlyBestUserPicks ?? [])}
      renderHeader={renderHeader}
      renderRow={renderRow}
      scrollRenderAheadDistance={500}
      renderFooter={renderFooter}
      onEndReached={endReachedHandler}
      onEndReachedThreshold={10}
      initialListSize={9}
      pageSize={9}
      className="today-user-pick-list"
    />
  )
})

export default TodayUserPick
